import type { FieldPreset } from '@components/RingilDataTable/v3/columns/columnModel';

/**
 * Keys, sidebar groups and presets of the supplier notification list's v3 field catalogue — plain
 * data, kept apart from the renderers in snFields.tsx so their consistency is unit-tested.
 */

/** Persisted in tabs, so these strings never change; a field that goes away is dropped on load. */
export const SnField = {
  ApplicationId: 'APPLICATION_ID',
  ResponsiblePerson: 'RESPONSIBLE_PERSON',
  SnType: 'SN_TYPE',
  Labels: 'LABELS',
  LinkedExpedition: 'LINKED_EXPEDITION',
  WaitingForPayment: 'WAITING_FOR_PAYMENT',
  WithoutTimeslots: 'WITHOUT_TIMESLOTS',
  GroupDemandProgress: 'GROUP_DEMAND_PROGRESS',
  GroupDemand: 'GROUP_DEMAND',
  LoadingLocation: 'LOADING_LOCATION',
  LoadingTimeslot: 'LOADING_TIMESLOT',
  UnloadingLocation: 'UNLOADING_LOCATION',
  UnloadingTimeslot: 'UNLOADING_TIMESLOT',
  Supplier: 'SUPPLIER',
  PartnerCompanyNote: 'PARTNER_COMPANY_NOTE',
  InfoExpandAll: 'INFO_EXPAND_ALL',
  ShipmentNumber: 'SHIPMENT_NUMBER',
  Cargo: 'CARGO',
  Attachments: 'ATTACHMENTS',
  InternalNote: 'INTERNAL_NOTE',
} as const;

const F = SnField;

/** Sidebar sections — the cells the fields come from. */
export const SnFieldGroup = {
  Notification: 'SupplierNotificationList.id',
  Expedition: 'SupplierNotificationList.expeditionAppId',
  Route: 'SupplierNotificationList.loadingUnloading',
  Supplier: 'SupplierNotificationList.supplierName',
  FurtherInformation: 'SupplierNotificationList.furtherInformation',
} as const;

/** Fallback texts of the group keys, for the sidebar section headings. */
export const SN_FIELD_GROUPS: { key: string; fallback: string }[] = [
  { key: SnFieldGroup.Notification, fallback: 'ID' },
  { key: SnFieldGroup.Expedition, fallback: 'Expedition' },
  { key: SnFieldGroup.Route, fallback: 'Loading/Unloading' },
  { key: SnFieldGroup.Supplier, fallback: 'Supplier' },
  { key: SnFieldGroup.FurtherInformation, fallback: 'Further information' },
];

/** Today's five cells as stacks of fields; the default tab is made of them, so it shows today's list. */
export const SN_PRESETS: FieldPreset[] = [
  {
    key: 'NOTIFICATION',
    width: 190,
    header: 'SupplierNotificationList.id',
    headerDefault: 'ID',
    group: SnFieldGroup.Notification,
    members: [F.ApplicationId, F.ResponsiblePerson, F.SnType, F.Labels, F.LinkedExpedition],
  },
  {
    key: 'EXPEDITION',
    width: 220,
    header: 'SupplierNotificationList.expeditionAppId',
    headerDefault: 'Expedition',
    group: SnFieldGroup.Expedition,
    members: [F.WaitingForPayment, F.WithoutTimeslots, F.GroupDemandProgress, F.GroupDemand],
  },
  {
    key: 'LOADING_UNLOADING',
    width: 240,
    header: 'SupplierNotificationList.loadingUnloading',
    headerDefault: 'Loading/Unloading',
    group: SnFieldGroup.Route,
    members: [F.LoadingLocation, F.LoadingTimeslot, F.UnloadingLocation, F.UnloadingTimeslot],
  },
  {
    key: 'SUPPLIER',
    width: 200,
    header: 'SupplierNotificationList.supplierName',
    headerDefault: 'Supplier',
    group: SnFieldGroup.Supplier,
    members: [F.Supplier, F.PartnerCompanyNote],
  },
  {
    key: 'FURTHER_INFORMATION',
    width: 260,
    header: 'SupplierNotificationList.furtherInformation',
    headerDefault: 'Further information',
    group: SnFieldGroup.FurtherInformation,
    members: [F.InfoExpandAll, F.ShipmentNumber, F.Cargo, F.Attachments, F.InternalNote],
  },
];

/** The "All" tab: today's list, cell for cell. */
export const SN_DEFAULT_COLUMNS: string[][] = ['NOTIFICATION', 'EXPEDITION', 'LOADING_UNLOADING', 'SUPPLIER', 'FURTHER_INFORMATION'].map(
  key => SN_PRESETS.find(preset => preset.key === key).members,
);

/**
 * Whether the further-information tags of a column show as expandable tags: only beside "Expand all",
 * which opens them all. Anywhere else a field shows its plain value.
 */
export const snInfoTagsCollapsible = (columnMembers: string[]): boolean => columnMembers.includes(SnField.InfoExpandAll);
